/**
 * 微信JS-SDK工具模块 (PC端适配版)
 * 提供微信环境检测、SDK初始化、分享、支付等功能
 * 注意：PC端不提供微信相关功能，此模块仅为API兼容性而保留
 */
import { systemRequest } from "@/qingyu";
import config from './config';
import { Helper } from './utils/helper';

// 创建一个空的wx对象，在PC端不实际使用微信功能
const wx = {
  config: () => console.warn('PC环境下不支持微信功能'),
  ready: (callback) => console.warn('PC环境下不支持微信功能'),
  error: () => console.warn('PC环境下不支持微信功能')
};

/**
 * 微信方法集合 (PC端适配版)
 */
class WechatService {
  constructor() {
    // SDK配置状态
    this.isConfigReady = false;
    
    // 存储签名数据
    this.signData = null;
    
    // SDK实例
    this.jweixin = null;
    
    // 记录上次初始化的URL
    this.lastInitUrl = '';
    
    // 默认需要注册的API列表
    this.defaultApiList = [
      'updateAppMessageShareData',    // 新版-自定义"分享给朋友"及"分享到QQ"
      'updateTimelineShareData',      // 新版-自定义"分享到朋友圈"及"分享到QQ空间"
      'chooseWXPay',                  // 微信支付
    ];
  }

  /**
   * 判断是否在微信环境中
   * @returns {boolean} 是否是微信浏览器
   */
  isWechat() {
    if (typeof window === 'undefined') return false;
    
    const ua = window.navigator.userAgent.toLowerCase();
    return ua.indexOf('micromessenger') !== -1;
  }

  /**
   * 判断是否在微信小程序中
   * @returns {boolean} 是否是微信小程序
   */
  isMiniProgram() {
    if (typeof window === 'undefined') return false;
    
    const ua = window.navigator.userAgent.toLowerCase();
    const isWeixin = ua.indexOf('micromessenger') !== -1;
    
    if (isWeixin && window.__wxjs_environment === 'miniprogram') {
      return true;
    }
    return false;
  }

  /**
   * 初始化JSSDK配置 (PC端不支持此功能)
   * @returns {Promise} 配置结果Promise
   */
  async initJssdk() {
    // PC端直接返回失败
    console.warn('PC环境下不支持微信JSSDK初始化');
    return Promise.reject(new Error('NOT_SUPPORTED_IN_PC'));
  }

  /**
   * 获取JSSDK签名 (PC端不支持此功能)
   * @returns {Promise} 签名数据Promise
   */
  async getSignature() {
    console.warn('PC环境下不支持获取微信JSSDK签名');
    return Promise.reject(new Error('NOT_SUPPORTED_IN_PC'));
  }

  /**
   * 检查SDK是否已经配置好 (PC端不支持此功能)
   * @returns {boolean} 是否已经配置好
   */
  checkConfigReady() {
    console.warn('PC环境下不支持微信SDK配置');
    return false;
  }

  /**
   * 分享接口 (PC端不支持此功能)
   * @param {Object} data 分享数据
   * @returns {Promise} 分享结果Promise
   */
  async share(data = {}) {
    // PC端提供复制链接的备选方案
    const shareUrl = data.link || window.location.href;
    
    // 显示分享对话框
    const confirmed = window.confirm(`微信分享功能仅在微信浏览器中可用。\n您可以复制链接后手动分享：\n${shareUrl}`);
    
    if (confirmed) {
      Helper.tool.copyText(shareUrl);
    }
    
    return Promise.resolve();
  }

  /**
   * 支付接口 (PC端不支持此功能)
   * @returns {Promise} 支付结果Promise
   */
  async pay() {
    console.warn('PC环境下不支持微信支付');
    Helper.tool.showToast({ title: '微信支付仅在微信环境中可用' });
    return Promise.reject(new Error('NOT_SUPPORTED_IN_PC'));
  }

  /**
   * 诊断当前环境
   * @returns {Object} 诊断结果
   */
  diagnose() {
    return {
      isWechat: this.isWechat(),
      isMiniProgram: this.isMiniProgram(),
      sdkReady: false,
      platform: 'pc'
    };
  }
}

// 创建单例实例
const wechatService = new WechatService();
export default wechatService;

/**
 * 获取微信服务实例 (组合式API)
 * @returns {WechatService} 微信服务实例
 */
export function useWechat() {
  return wechatService;
}